import {
  InputSelectionListChangeEvent,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import {
  ConversationLanguageCode,
  conversationLanguages,
} from 'lib-shared-languages';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LanguageSelectionListValue } from './useLanguageSelectionList.types';
import { languageCodesToSelectionListValue } from './utils';

const languageCodes: readonly ConversationLanguageCode[] =
  conversationLanguages.map((l) => l.code);

type UseLanguageSelectionListResult<
  TChangeEvent extends
    | InputSelectionListChangeEvent<LanguageSelectionListValue>
    | SelectionListChangeEvent<LanguageSelectionListValue>,
> = {
  /** The selection list language items. */
  readonly languages: readonly LanguageSelectionListValue[];
  /** The selection list selected language items. */
  readonly selectedLanguages: readonly LanguageSelectionListValue[];
  /** The function for clearing the selection. */
  readonly clearSelection: () => void;
  /** The callback function of the selection list onChange callback. */
  readonly onChange: (event: TChangeEvent) => void;
};

export type UseLanguageSelectionListOptions = {
  /** The selected languages codes. */
  readonly selectedLanguageCodes?: readonly ConversationLanguageCode[];
};

/** A hook for managing the state of a language selection list. */
export function useLanguageSelectionList<
  TChangeEvent extends
    | InputSelectionListChangeEvent<LanguageSelectionListValue>
    | SelectionListChangeEvent<LanguageSelectionListValue>,
>(
  options: UseLanguageSelectionListOptions,
): UseLanguageSelectionListResult<TChangeEvent> {
  // We need to memo the options because of interdependency on consumption.
  const memoOptions = useMemo(() => options, [options]);
  const { selectedLanguageCodes } = memoOptions;
  const [selectedLanguages, setSelectedLanguages] = useState<
    readonly LanguageSelectionListValue[]
  >([]);

  const languages = useMemo(
    () => languageCodesToSelectionListValue(languageCodes),
    [],
  );

  useEffect(() => {
    setSelectedLanguages(
      languageCodesToSelectionListValue(selectedLanguageCodes ?? []),
    );
  }, [selectedLanguageCodes]);

  const clearSelection = useCallback(() => {
    setSelectedLanguages([]);
  }, []);

  const onChange = useCallback<
    UseLanguageSelectionListResult<TChangeEvent>['onChange']
  >(({ selectedValues }) => {
    setSelectedLanguages(selectedValues);
  }, []);

  return useMemo(
    () => ({
      languages,
      selectedLanguages,
      clearSelection,
      onChange,
    }),
    [clearSelection, languages, onChange, selectedLanguages],
  );
}
